import { bool, fail, isRecord, list, optionalText, sameResource, text } from '../../../shared/api/onecEnvelope'
import type {
  OneCStructure,
  StructureLink,
  StructureLinkState,
  StructureOrganization,
  StructureUnit,
  StructureUnitKind,
} from '../model/types'

/**
 * Декодер `OneCStructureDto` (Resource.OneC.Contracts/Structure). Каждая ссылка
 * внутри ответа обязана быть про ЭТУ базу: элемент чужой базы с тем же GUID —
 * ошибка ответа, а не склад не той базы (D-064). GUID — в нижнем регистре.
 * Структура читается из 1С на лету (RP-LSB1): `freshness`/`staleReason` служба
 * отдаёт всегда `fresh`/`null`, клиент их не читает; 1С молчит — 503 с её
 * текстом, а не прежняя структура.
 */
const UNIT_KINDS: readonly StructureUnitKind[] = ['department', 'warehouse', 'other']
const LINK_STATES: readonly StructureLinkState[] = ['linked', 'empty', 'unresolved', 'unsupported']

function record(raw: unknown, field: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${field} — не объект`)
}

function refKey(raw: unknown, field: string, resourceId: string): string {
  const ref = record(raw, field)
  const owner = isRecord(ref.resourceId) ? text(ref.resourceId.value, `${field}.resourceId.value`) : text(ref.resourceId, `${field}.resourceId`)
  if (!sameResource(owner, resourceId)) fail(`${field} ссылается на другую базу`)
  return text(ref.remoteKey, `${field}.remoteKey`).toLowerCase()
}

function oneOf<T extends string>(value: unknown, field: string, allowed: readonly T[]): T {
  const raw = text(value, field)
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fail(`поле ${field} — неизвестное значение «${raw}»`)
}

function link(raw: unknown, field: string, resourceId: string): StructureLink {
  const item = record(raw, field)
  const state = oneOf(item.state, `${field}.state`, LINK_STATES)
  const key = item.ref === undefined || item.ref === null ? null : refKey(item.ref, `${field}.ref`, resourceId)
  // Связанная или оборванная ссылка без ключа — не связь, а ошибка ответа.
  if ((state === 'linked' || state === 'unresolved') && key === null) fail(`${field}: связь без ссылки`)
  return { state, key: state === 'linked' || state === 'unresolved' ? key : null }
}

export function decodeStructure(resourceId: string) {
  return (raw: unknown): OneCStructure => {
    const data = record(raw, 'data')
    const relations = record(data.relations, 'relations')
    return {
      organizations: list(data.organizations, 'organizations', (entry, index): StructureOrganization => {
        const item = record(entry, `organizations[${index}]`)
        return {
          key: refKey(item.ref, `organizations[${index}].ref`, resourceId),
          name: text(item.name, 'name'),
          code: optionalText(item.code, 'code'),
        }
      }),
      units: list(data.units, 'units', (entry, index): StructureUnit => {
        const item = record(entry, `units[${index}]`)
        const field = `units[${index}]`
        return {
          key: refKey(item.ref, `${field}.ref`, resourceId),
          name: text(item.name, 'name'),
          code: optionalText(item.code, 'code'),
          kind: oneOf(item.kind, `${field}.kind`, UNIT_KINDS),
          nativeType: optionalText(item.nativeType, 'nativeType'),
          organization: link(item.organization, `${field}.organization`, resourceId),
          department: link(item.department, `${field}.department`, resourceId),
          parent: link(item.parent, `${field}.parent`, resourceId),
          // Вид цен — тоже ссылка этой базы: чужой rid — ошибка ответа, как у любой ссылки.
          hasRetailPriceType: item.retailPriceType !== null && item.retailPriceType !== undefined && refKey(item.retailPriceType, `${field}.retailPriceType`, resourceId) !== '',
        }
      }),
      relations: {
        unitOrganization: bool(relations.unitOrganization, 'relations.unitOrganization'),
        unitHierarchy: bool(relations.unitHierarchy, 'relations.unitHierarchy'),
        departments: bool(relations.departments, 'relations.departments'),
        unitDepartment: bool(relations.unitDepartment, 'relations.unitDepartment'),
      },
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}
